import { FormFieldSpec, LookupDefault, MessagePart } from './aiAssistantModel'
import { PlaceData } from '../common/googlePlacesAutoCompleteModel'

export type FormValues = Record<string, unknown>

/**
 * The `{id, label}` prefill of a lookup field, or `null` when it has none. A
 * lookup's stored value is just the id; the label is only for display.
 */
export function lookupDefault(field: FormFieldSpec): LookupDefault | null {
  const def = field.default
  if (
    field.type === 'lookup' &&
    typeof def === 'object' &&
    def !== null &&
    'id' in def
  ) {
    return def as LookupDefault
  }
  return null
}

/**
 * Seed a form's field values before the user edits anything: a lookup stores
 * its default's id (or blank), a checkbox defaults to `false` (or its provided
 * default), everything else to an empty string (or its provided default).
 */
export function initialFormValues(fields: FormFieldSpec[]): FormValues {
  const values: FormValues = {}
  for (const field of fields) {
    if (field.type === 'lookup') {
      const def = lookupDefault(field)
      values[field.name] = def ? def.id : ''
    } else if (field.default !== undefined && field.default !== null) {
      values[field.name] = field.default
    } else if (field.type === 'checkbox') {
      values[field.name] = false
    } else {
      values[field.name] = ''
    }
  }
  return values
}

function isBlank(value: unknown): boolean {
  return value === undefined || value === null || String(value).trim() === ''
}

/**
 * The string to show in a text-like control for a field value. Primitives
 * render as-is; a stray object (e.g. a model put an `{id, label}` default on a
 * plain text field instead of a lookup) shows its `label` rather than the
 * useless `"[object Object]"`.
 */
export function fieldDisplayValue(value: unknown): string {
  if (value === null || value === undefined) {
    return ''
  }
  if (typeof value === 'object') {
    const label = (value as { label?: unknown }).label
    return typeof label === 'string' ? label : ''
  }
  return String(value)
}

/**
 * The string to show for a value the user already submitted, in a read-only
 * history. A checkbox always reads Yes or No (never blank), a select shows the
 * chosen option's label, and a lookup shows its prefill's label when the user
 * kept it — otherwise only the id was recorded, so the id is what we can show.
 */
export function submittedDisplayValue(field: FormFieldSpec, value: unknown): string {
  if (field.type === 'checkbox') {
    return value ? 'Yes' : 'No'
  }
  if (value === undefined || value === null || value === '') {
    return '—'
  }
  if (field.type === 'select') {
    const option = field.options?.find((o) => o.value === String(value))
    return option ? option.label : String(value)
  }
  if (field.type === 'lookup') {
    const def = lookupDefault(field)
    if (def && def.id === value) {
      return def.label
    }
    return String(value)
  }
  return fieldDisplayValue(value)
}

/**
 * Names of required fields the user has not satisfied yet: a required checkbox
 * must be checked; any other required field must be non-blank.
 */
export function missingRequiredFields(
  fields: FormFieldSpec[],
  values: FormValues
): string[] {
  return fields
    .filter((field) => {
      if (!field.required) {
        return false
      }
      if (field.type === 'checkbox') {
        return values[field.name] !== true
      }
      return isBlank(values[field.name])
    })
    .map((field) => field.name)
}

const US_ZIP = /^\d{5}(-\d{4})?$/

// Pragmatic email shape: some non-space/@ text, an @, a domain, a dot, a TLD.
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** A field is a zip code if its name looks like one (there is no zip type). */
function isZipField(field: FormFieldSpec): boolean {
  return (
    field.type !== 'checkbox' &&
    field.type !== 'select' &&
    field.type !== 'lookup' &&
    /zip|postal/i.test(field.name)
  )
}

/**
 * Format-validate a single field's value, returning an error message or
 * `null` when it is acceptable. Blank values pass here — emptiness of a
 * required field is reported by {@link missingRequiredFields}, not this.
 *
 * Currently covers email addresses (any `email` field), phone numbers (any
 * `tel` field), and zip codes (any field whose name looks like a zip/postal
 * code).
 */
export function validateField(field: FormFieldSpec, value: unknown): string | null {
  if (isBlank(value)) {
    return null
  }
  const text = String(value).trim()

  if (field.type === 'email') {
    return EMAIL.test(text) ? null : 'Enter a valid email address.'
  }

  if (field.type === 'tel') {
    const digits = text.replace(/\D/g, '')
    const isTenDigits = digits.length === 10
    const isElevenWithLeadingOne = digits.length === 11 && digits.startsWith('1')
    if (isTenDigits || isElevenWithLeadingOne) {
      return null
    }
    return 'Enter a 10-digit phone number.'
  }

  if (isZipField(field)) {
    if (US_ZIP.test(text)) {
      return null
    }
    return 'Enter a 5-digit ZIP code (e.g. 01720).'
  }

  return null
}

/** Error messages for every field that fails {@link validateField}, by name. */
export function fieldErrors(
  fields: FormFieldSpec[],
  values: FormValues
): Record<string, string> {
  const errors: Record<string, string> = {}
  for (const field of fields) {
    const error = validateField(field, values[field.name])
    if (error) {
      errors[field.name] = error
    }
  }
  return errors
}

/**
 * A field is an address-lookup field if its name looks like a street address
 * line. Matches `address1` / `street` but deliberately not a bare `address`
 * (which would catch `email_address`); there is no dedicated address type.
 */
export function isAddressField(field: FormFieldSpec): boolean {
  return (
    field.type !== 'checkbox' &&
    field.type !== 'select' &&
    field.type !== 'lookup' &&
    /address1|street/i.test(field.name)
  )
}

/**
 * A dependent lookup (one with `depends_on`) is blocked until its parent field
 * has a value: it can't be searched without one, so the control is disabled
 * and the user must fill the parent first.
 */
export function isLookupBlockedByParent(
  field: FormFieldSpec,
  values: FormValues
): boolean {
  return (
    field.type === 'lookup' &&
    !!field.depends_on &&
    isBlank(values[field.depends_on])
  )
}

/**
 * The 50 US states plus the District of Columbia, as `{ code, name }`, where
 * `code` is the 2-letter postal abbreviation submitted as the field value.
 */
export const US_STATES: { code: string; name: string }[] = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
]

/**
 * A field is a US-state field if its name looks like a state/province — the
 * same fields {@link addressAutofill} fills with the 2-letter `stateShort`, so
 * they render as a states dropdown whose value is that 2-letter code. An
 * explicit `select` (the model supplied its own options) is left as-is.
 */
export function isStateField(field: FormFieldSpec): boolean {
  return (
    field.type !== 'checkbox' &&
    field.type !== 'select' &&
    field.type !== 'lookup' &&
    /state|province/i.test(field.name)
  )
}

/**
 * Reset every lookup field that depends on `changedName`, directly or through
 * a chain, to blank — a stale child selection must never survive a change to
 * the parent it was scoped by. Returns the same object when nothing changed.
 */
export function clearDependentLookups(
  fields: FormFieldSpec[],
  values: FormValues,
  changedName: string
): FormValues {
  let out = values
  for (const field of fields) {
    if (field.type === 'lookup' && field.depends_on === changedName) {
      if (!isBlank(out[field.name])) {
        out = { ...out, [field.name]: '' }
      }
      out = clearDependentLookups(fields, out, field.name)
    }
  }
  return out
}

/**
 * Values to apply when the user picks an address from the lookup: the address
 * field gets the street line, and any sibling city/state/zip fields present in
 * the same form are filled from the selected place. State uses the 2-letter
 * code. Only fields that exist in the form are returned, so no unknown keys
 * leak into the submitted payload.
 */
export function addressAutofill(
  fields: FormFieldSpec[],
  addressFieldName: string,
  place: PlaceData
): FormValues {
  const out: FormValues = {}
  for (const field of fields) {
    if (field.name === addressFieldName) {
      out[field.name] = place.address1
    } else if (/city|locality/i.test(field.name)) {
      out[field.name] = place.city
    } else if (/state|province/i.test(field.name)) {
      out[field.name] = place.stateShort
    } else if (/zip|postal/i.test(field.name)) {
      out[field.name] = place.zip
    }
  }
  return out
}

/**
 * Build the payload sent to the write tool: checkboxes are always included as
 * booleans; number fields are coerced to numbers; blank optional fields are
 * omitted so the tool sees an absent value rather than an empty string.
 */
export function buildSubmitValues(
  fields: FormFieldSpec[],
  values: FormValues
): FormValues {
  const out: FormValues = {}
  for (const field of fields) {
    const value = values[field.name]
    if (field.type === 'checkbox') {
      out[field.name] = value === true
      continue
    }
    if (isBlank(value)) {
      continue
    }
    if (field.type === 'number') {
      out[field.name] = Number(value)
    } else {
      out[field.name] = value
    }
  }
  return out
}

/**
 * Whether a collect-mode form has been submitted.
 *
 * A collect form names no write tool, so submitting it executes nothing: the
 * harness replaces the form's own placeholder result with the structured
 * `{"prompt": ..., "values": [...]}` JSON. That lands on `tool_call_id ===
 * formId` exactly — unlike a write form, whose result carries the derived
 * `${form_id}-<suffix>` id of the write it fed.
 *
 * During the pause the only result on that id is the placeholder, which is
 * plain text and fails the parse, so the form stays live until it is really
 * submitted. Mirrors `questionSetResolved`, which keys on `answers` — but the
 * key test here cannot be `'values' in data`: `Array.prototype.values` exists,
 * so `in` reports true for any JSON array and a result that merely happened to
 * be a list would lock the form. Hence the array guard and the own-property
 * check.
 *
 * @param parts - The message's parts, in stream order.
 * @param formId - The `form_id` of the form being tested.
 * @returns True once the submitted values are present, else false.
 */
export function collectFormResolved(parts: MessagePart[], formId: string): boolean {
  return parts.some((part) => {
    if (part.type !== 'tool_result' || part.tool_call_id !== formId) {
      return false
    }
    try {
      const data: unknown = JSON.parse(part.content)
      return (
        typeof data === 'object' &&
        data !== null &&
        !Array.isArray(data) &&
        Object.prototype.hasOwnProperty.call(data, 'values')
      )
    } catch {
      return false
    }
  })
}

// The lookup entities the assistant has a create tool for. A lookup that finds
// nothing can offer to create one of these; the rest are setup data, and an
// offer there would promise something no tool can do (#466).
const CREATABLE_LOOKUP_ENTITIES = new Set([
  'customer',
  'job',
  'property',
  'work_area',
  'work_area_group',
])

/** Whether a lookup field's entity is one the assistant can create. */
export function isCreatableLookupEntity(
  entity: string | null | undefined
): boolean {
  return entity != null && CREATABLE_LOOKUP_ENTITIES.has(entity)
}

/** A lookup entity as a person would say it: `work_area_group` → work area group. */
export function lookupEntityNoun(entity: string): string {
  return entity.replace(/_/g, ' ')
}

/**
 * The message that asks the assistant to create the record the user typed.
 *
 * Deliberately a plain request rather than a structured command: the create
 * flow already exists in chat, complete with its own follow-up questions and
 * approval card, and this hands off to it rather than reproducing it.
 */
export function createLookupRequestMessage(
  entity: string,
  query: string
): string {
  return `Create a new ${lookupEntityNoun(entity)} named "${query.trim()}".`
}
